import React, { useRef, useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory, useRouteMatch } from "react-router-dom"
import { generatePaginationOptions, updateUrlQueryParam } from "lib/helper"
import { selectLoading } from "modules/notification"
import { getDealerInventoryBoxListing, resetDealerInventoryListing } from "modules/stockflow/redux"
import DealerBoxListPanelComponent from "./dealerBoxListPanel.component"
import { fetchTagsDropdown } from "modules/serial-number/redux"
import {AccessRights} from "lib/constants/accessRights";
import {canAccess} from "../../../../profile/redux/selector/profile.selector";

function DealerBoxListPanelContainer() {
  const dispatch = useDispatch()
  const history = useHistory()
  const match = useRouteMatch()

  const id = match.params.dealerId
  const lang = useSelector(state => state.constant.languages)
  const query = new URLSearchParams(window.location.search);
  const hasStockflowInventoryViewAccessRight = useSelector(state =>
    canAccess(state, AccessRights.TrackcoPortal.DEALER_INVENTORY));

  const isFetching = useSelector(state => selectLoading(state, getDealerInventoryBoxListing.typePrefix))

  const totalFiltered = useSelector(state => state.stockflowDealer.inventoryBoxList.totalFiltered)
  const totalRecords = useSelector(state => state.stockflowDealer.inventoryBoxList.totalRecords)
  const boxList = useSelector(state => state.stockflowDealer.inventoryBoxList.list)

  const isError = useSelector(state => state.stockflowDealer.inventoryBoxList.isError)
  const tags = useSelector(state => state.serialNumberTag.tagsDropdown)
  const isSearchResult = boxList.length < totalRecords
  const [paginationOptions, setPaginationOptions] = useState([]);
  const [isFilterUpdated, setIsFilterUpdated] = useState(false)
  const [tableFilter, setTableFilter] = useState({
    page: 0,
    rowsPerPage: 25,
    search: "",
    searchColumn: "",
    order: "desc",
    orderBy: "updated_at",
    products: [],
  })

  const tableFilterInitialRef = useRef(true)

  useEffect(() => {
    return () => {
      dispatch(resetDealerInventoryListing())
    }
  }, [dispatch])

  useEffect(() => {
    const isFilter = query.has("is_filter")
    let filter = sessionStorage.getItem(`dealer_box_filter_${id}`);

    let temp = {...tableFilter}
    if (isFilter && filter) {
      temp = JSON.parse(filter)
      setIsFilterUpdated(true)
    }

    if (isFilter) {
      updateUrlQueryParam(null, ["is_filter"])
    }
    sessionStorage.removeItem(`dealer_box_filter_${id}`)

    setTableFilter(temp)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    dispatch(fetchTagsDropdown())
  }, [dispatch])

  useEffect(() => {
    if (!tableFilterInitialRef.current) {
      getData()
    }
    tableFilterInitialRef.current = false
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableFilter])

  useEffect(() => {
    setPaginationOptions(generatePaginationOptions(totalFiltered))
  }, [totalFiltered])

  const getData = () => {
    dispatch(getDealerInventoryBoxListing({
      dealer: id,
      length: tableFilter.rowsPerPage,
      start: tableFilter.rowsPerPage * tableFilter.page,
      order: tableFilter.order,
      order_by: tableFilter.orderBy,
      filter: {
        search: tableFilter.search,
        search_by: tableFilter.searchColumn,
        products: tableFilter.products,
      }
    }))
  }

  const handleChangePage = (event, newPage) => {
    let newData = {
      ...tableFilter,
      page: newPage,
    }
    setIsFilterUpdated(true)
    setTableFilter(newData)
  }

  const handleChangeRowsPerPage = event => {
    let newData = {
      ...tableFilter,
      page: 0,
      rowsPerPage: event.target.value,
    }
    setIsFilterUpdated(true)
    setTableFilter(newData)
  }

  const handleSearch = (filter) => {
    const { search, searchColumn, products } = filter

    let newData = {
      ...tableFilter,
      page: 0,
      search: search,
      searchColumn: searchColumn,
      products: products,
    }

    setIsFilterUpdated(true)
    setTableFilter(newData)
  }

  const clearSearch = () => {
    let newData = {
      ...tableFilter,
      page: 0,
      search: "",
      searchColumn: "",
      products: [],
    }
    setIsFilterUpdated(false)
    setTableFilter(newData)
  }

  const changeOrder = (orderBy, order) => {
    let newData = {
      ...tableFilter,
      order,
      orderBy
    }
    setIsFilterUpdated(true)
    setTableFilter(newData)
  }

  const handleViewDetail = (url) => {
    if (isFilterUpdated) {
      updateUrlQueryParam({ is_filter: true })
      sessionStorage.setItem(`dealer_box_filter_${id}`, JSON.stringify(tableFilter));
    }
    history.push(url)
  }

  return (
    <DealerBoxListPanelComponent
      hasStockflowInventoryViewAccessRight={hasStockflowInventoryViewAccessRight}
      id={id}
      boxes={boxList}
      isFetching={isFetching}
      isError={isError}
      handleChangePage={handleChangePage}
      handleChangeRowsPerPage={handleChangeRowsPerPage}
      page={tableFilter.page}
      rowsPerPage={tableFilter.rowsPerPage}
      totalFiltered={totalFiltered}
      handleSearch={handleSearch}
      clearSearch={clearSearch}
      handleReload={getData}
      isSearchResult={isSearchResult}
      paginationOptions={paginationOptions}
      tableFilter={tableFilter}
      lang={lang}
      changeOrder={changeOrder}
      tags={tags}
      handleViewDetail={handleViewDetail}
    />
  )
}

export default DealerBoxListPanelContainer
